import React, { useState } from "react";
import { Row, Col, Card, DatePicker, Button, message, Table, Tag, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  DownloadOutlined,
  PhoneOutlined
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import AdminBreadCrumb from "components/AdminBreadCrumb";
import moment from "moment";
import request from "helper/request";
import "./DriverCallLogReport.scss";

const { RangePicker } = DatePicker;

const breadCrumbs = [
  { text: "REPORTS", path: "/admin/reports" },
  { text: "DRIVER CALL LOGS" }
];

const PAGE_SIZE = 20;
const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

const CONTACT_TYPE_LABELS = {
  contact: "Recipient",
  mainSupport: "Main support"
};

// Mirrors the labels drivers see in the app; unknown types are split from camelCase.
const contactTypeLabel = (type) => {
  if (!type) return "";
  if (CONTACT_TYPE_LABELS[type]) return CONTACT_TYPE_LABELS[type];
  const words = type
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
};

const formatDateTime = (val) => (val ? moment(val).format(DATE_TIME_FORMAT) : "");

const shortId = (val) => (val ? val.substring(0, 8).toUpperCase() : "");

// What we know about how the attempt ended, from the app's lifecycle reports.
const outcome = (row) => {
  if (!row.returnedToAppAt) return "No report";
  if (!row.leftAppAt) return "Never left app";
  return "Left app";
};

const DriverCallLogReport = () => {
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days").startOf("day"),
    moment().endOf("day")
  ]);

  const [tableData, setTableData] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Track the last submitted filters so pagination and export use the same params
  const [submittedFilters, setSubmittedFilters] = useState(null);

  const fetchPage = async (filters, page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: filters.startDate,
        endDate: filters.endDate,
        page,
        limit: PAGE_SIZE
      });
      const data = await request.get(
        `/admin/reports/driver-call-logs?${params.toString()}`
      );
      setTableData(data?.data || []);
      setTotal(data?.total || 0);
      setCurrentPage(page);
    } catch {
      message.error("Failed to load driver call logs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.warning("Please select a date range");
      return;
    }

    // Send full timestamps so the range follows the admin's local days
    const filters = {
      startDate: dateRange[0].clone().startOf("day").toISOString(),
      endDate: dateRange[1].clone().endOf("day").toISOString(),
      startLabel: dateRange[0].format("YYYY-MM-DD"),
      endLabel: dateRange[1].format("YYYY-MM-DD")
    };

    setSubmittedFilters(filters);
    await fetchPage(filters, 1);
  };

  const handleTableChange = (pagination) => {
    if (!submittedFilters) return;
    fetchPage(submittedFilters, pagination.current);
  };

  const handleExportCSV = async () => {
    if (!submittedFilters) {
      message.warning("Please generate a report first");
      return;
    }

    setExportLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: submittedFilters.startDate,
        endDate: submittedFilters.endDate
      });
      const rows = await request.get(
        `/admin/reports/driver-call-logs/export?${params.toString()}`
      );

      if (!rows || rows.length === 0) {
        message.warning("No data to export");
        return;
      }

      const filename = `driver-call-logs-${submittedFilters.startLabel}_${submittedFilters.endLabel}.csv`;

      const headers = [
        "Time",
        "Driver Name",
        "Driver ID",
        "Driver Email",
        "Method",
        "Contact",
        "Phone Number",
        "Recipient Name",
        "Location Name",
        "Stop Type",
        "Client",
        "Job Number",
        "Left App At",
        "Returned To App At",
        "Time Away (seconds)",
        "Time Away",
        "Outcome",
        "Platform",
        "Route ID",
        "Route Location ID",
        "Log ID"
      ];

      const escape = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;

      const csvRows = [
        headers.join(","),
        ...rows.map((row) =>
          [
            formatDateTime(row.initiatedAt),
            row.driverName,
            row.driverId,
            row.driverEmail,
            row.method,
            contactTypeLabel(row.contactType),
            row.phoneNumber,
            row.contactName,
            row.locationName,
            row.stopType,
            row.clientName,
            row.jobNumber,
            formatDateTime(row.leftAppAt),
            formatDateTime(row.returnedToAppAt),
            row.awaySeconds,
            formatDuration(row.awaySeconds),
            outcome(row),
            row.platform,
            row.routeId,
            row.routeLocationId,
            row.id
          ]
            .map(escape)
            .join(",")
        )
      ];

      const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.setAttribute("href", URL.createObjectURL(blob));
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Exported ${rows.length} records`);
    } catch {
      message.error("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const columns = [
    {
      title: "Time",
      dataIndex: "initiatedAt",
      key: "initiatedAt",
      width: 175,
      render: (val) => formatDateTime(val) || "-"
    },
    {
      title: "Driver",
      dataIndex: "driverName",
      key: "driverName",
      width: 200,
      render: (val, record) => (
        <Tooltip title={record.driverId}>
          {val || "-"}{" "}
          <span style={{ color: "#999", fontSize: 12 }}>{shortId(record.driverId)}</span>
        </Tooltip>
      )
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
      width: 90,
      render: (val) =>
        val === "call" ? <Tag color="blue">Call</Tag> : <Tag color="purple">Text</Tag>
    },
    {
      title: "Contact",
      dataIndex: "contactType",
      key: "contactType",
      width: 130,
      render: (val) => contactTypeLabel(val) || "-"
    },
    {
      title: "Time Away",
      dataIndex: "awaySeconds",
      key: "awaySeconds",
      width: 140,
      render: (val, record) =>
        val !== null && val !== undefined ? (
          formatDuration(val)
        ) : (
          <span style={{ color: "#999" }}>{outcome(record)}</span>
        )
    },
    {
      title: "Recipient",
      dataIndex: "contactName",
      key: "contactName",
      width: 180,
      ellipsis: true,
      render: (val, record) => val || record.locationName || "-"
    },
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
      width: 160,
      ellipsis: true,
      render: (val) => val || "-"
    },
    {
      title: "Job",
      dataIndex: "jobNumber",
      key: "jobNumber",
      width: 120,
      render: (val, record) => val || shortId(record.routeLocationId) || "-"
    },
    {
      title: "Route",
      dataIndex: "routeId",
      key: "routeId",
      width: 110,
      render: (val) =>
        val ? (
          <a
            href={`/admin/routes/${val}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/admin/routes/${val}`);
            }}
          >
            {shortId(val)}
          </a>
        ) : (
          "-"
        )
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      width: 90,
      render: (val) => val || "-"
    }
  ];

  return (
    <div className="driver-call-log-report">
      <AdminBreadCrumb breadCrumbs={breadCrumbs} />

      <div className="driver-call-log-report-content">
        <div className="driver-call-log-report-header">
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => history.push("/admin/reports")}
            style={{ padding: 0, marginBottom: "16px" }}
          >
            Back to Reports
          </Button>
          <h1 style={{ color: "#1890ff", marginBottom: "8px" }}>Driver Call Logs</h1>
          <p style={{ color: "#666", marginBottom: "32px" }}>
            Calls and texts drivers started to stop contacts from the driver app. Time
            Away is how long the driver was out of the app afterwards, an approximation of
            the call length.
          </p>
        </div>

        <Card className="filters-card" style={{ marginBottom: "24px" }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: "8px" }}>
                <CalendarOutlined
                  style={{ color: "#1890ff", fontSize: "14px", marginRight: "8px" }}
                />
                <span style={{ fontWeight: 500 }}>Date Range *</span>
              </div>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
                placeholder={["Start Date", "End Date"]}
                style={{ width: "100%" }}
                disabledDate={(current) => current && current > moment().endOf("day")}
              />
            </Col>

            <Col xs={24} md={4} style={{ display: "flex", alignItems: "flex-end" }}>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading}
                disabled={!dateRange || dateRange.length !== 2}
                icon={<PhoneOutlined />}
                style={{ width: "100%" }}
              >
                Generate
              </Button>
            </Col>
          </Row>
        </Card>

        {submittedFilters && (
          <div className="report-results">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px"
              }}
            >
              <h2 style={{ color: "#1890ff", margin: 0 }}>
                {moment(submittedFilters.startLabel).format("MMM DD, YYYY")} &mdash;{" "}
                {moment(submittedFilters.endLabel).format("MMM DD, YYYY")}
                <span style={{ color: "#666", fontSize: "16px", marginLeft: "12px" }}>
                  ({total} record{total !== 1 ? "s" : ""})
                </span>
              </h2>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportCSV}
                loading={exportLoading}
                disabled={total === 0}
              >
                Export CSV
              </Button>
            </div>

            <Card>
              <Table
                columns={columns}
                dataSource={tableData}
                rowKey="id"
                loading={loading}
                scroll={{ x: 1400 }}
                pagination={{
                  current: currentPage,
                  pageSize: PAGE_SIZE,
                  total,
                  showTotal: (t) => `Total ${t} records`,
                  showSizeChanger: false
                }}
                onChange={handleTableChange}
              />
            </Card>
          </div>
        )}

        {!submittedFilters && !loading && (
          <Card className="empty-state">
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <PhoneOutlined
                style={{ fontSize: "48px", color: "#d9d9d9", marginBottom: "16px" }}
              />
              <h3 style={{ color: "#666" }}>No Report Generated</h3>
              <p style={{ color: "#999" }}>Select a date range, then click Generate</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DriverCallLogReport;
